import { YousignFrame } from '@components'

import { Button, Flex, notify, SkeletonBox, Space, Spacing, Text } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useSegment } from '../../../../hooks'
import { getClassName } from '../../../../utils/getClassName'
import { QuickStartStep } from '../../../quickStart/enums/quickStartStep'
import { useQuickStartProgress } from '../../../quickStart/hooks'
import { useConfirmCerfaLinkOnSign } from '../../hooks/useConfirmCerfaLinkOnSign'
import { useEInvoicingSetup } from '../../hooks/useEInvoicingSetup'
import { useGetCerfaSignatureLink } from '../../hooks/useGetCerfaSignatureLink'
import { EInvoicingStepContainer } from '../EInvoicingStep/elements/EInvoicingStepContainer'
import * as Styled from './styles'

export const EInvoicingSignature = (): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { navigateToEInvoices } = useEInvoicingSetup()
  const { markStepDone } = useQuickStartProgress()
  const { data, isProcessing: isSignatureLinkBeingFetched } = useGetCerfaSignatureLink({})

  const { confirmCerfaLinkOnSign, isProcessing: isConfirmingCerfaLinkOnSign } = useConfirmCerfaLinkOnSign({
    onError: () => {
      trackError('Error confirming Cerfa link on sign')
      notify({
        id: NotificationKeys.EInvoiceKYCSignatureError,
        message: t('e_invoice.kyc.step.signature.notify.error'),
        variant: 'error',
      })
      navigateToEInvoices()
    },
    onSuccess: () => {
      notify({
        id: NotificationKeys.EInvoiceKYCSignatureError,
        message: t('e_invoice.kyc.step.signature.notify.success'),
        variant: 'success',
      })
      track('xxx - e-invoicing: Completed Yousign Signature (Client)')
      markStepDone(QuickStartStep.EInvoicing)
    },
  })

  const [isSignatureProvided, setIsSignatureProvided] = useState(false)

  const handleOnSuccessfulSignature = useCallback(() => {
    if (data) {
      confirmCerfaLinkOnSign(data.signatureRequestId)
    }
    setIsSignatureProvided(true)
  }, [confirmCerfaLinkOnSign, data])

  const handleOnErrorSignature = useCallback(() => {
    setIsSignatureProvided(false)
  }, [])

  const handleContinueToNextStep = useCallback(async () => {
    navigateToEInvoices()
  }, [navigateToEInvoices])

  return (
    <EInvoicingStepContainer>
      <Flex gap={Spacing.S32} justifyContent="space-between">
        <Styled.SignatureDocumentContainer
          className={getClassName('dark-theme-suspend')}
          isExpanded={isSignatureProvided}
        >
          {isSignatureLinkBeingFetched ? (
            <SkeletonBox width={650} height={700} />
          ) : (
            <Styled.IframeContainer>
              <YousignFrame
                height="85vh"
                signatureLink={data?.signatureLink ?? ''}
                onSuccess={handleOnSuccessfulSignature}
                onError={handleOnErrorSignature}
              />
              {isSignatureProvided && (
                <Styled.ButtonPositioner>
                  <Button
                    disabled={isConfirmingCerfaLinkOnSign}
                    loading={isConfirmingCerfaLinkOnSign}
                    onClick={handleContinueToNextStep}
                  >
                    {t('e_invoice.kyc.step.signature.cta.primary')}
                  </Button>
                </Styled.ButtonPositioner>
              )}
            </Styled.IframeContainer>
          )}
        </Styled.SignatureDocumentContainer>
        {!isSignatureProvided && (
          <Flex flexDirection="column" width="25vw" flexGrow={0}>
            <Flex width="100%" justifyContent="flex-end">
              <Button disabled={isSignatureLinkBeingFetched} icon="x" variant="outlined" to="/">
                {t('e_invoice.kyc.step.signature.close.button')}
              </Button>
            </Flex>
            <Space size="s40" />
            <Flex flexDirection="column" gap={Spacing.S32} justifyContent="center" alignItems="center">
              <Text variant="h1">{t('e_invoice.kyc.step.signature.title')}</Text>
              <Text>{t('e_invoice.kyc.step.signature.subtitle')}</Text>
            </Flex>
            <Space size="s32" />
          </Flex>
        )}
      </Flex>
    </EInvoicingStepContainer>
  )
}
